import { Column, ColumnState, GridApi } from 'ag-grid-community';
import { GROUP_COLUMN_PREFIX, HEADER_SORT_CYCLE, HeaderSortType } from '../Constants';
import {
  CustomSortConfig,
  Hierarchy,
  HierarchyColumnConfig,
  OrderConfig,
  SortDirection,
} from 'common/visualizations/vif';
import find from 'lodash/find';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { RenderUnindentedHeaderProps } from './customSortRenders';

export const getColumnSortState = (vifOrderConfig: OrderConfig[]) => {
  const columnSortState: ColumnState[] = vifOrderConfig.map((col, orderIndex) => {
    return {
      colId: col.columnName,
      sort: col.ascending ? 'asc' : 'desc',
      sortIndex: orderIndex,
    };
  });
  return columnSortState;
};

export interface IColumnGroupResetParams {
  columnState: ColumnState[] | undefined;
  vifOrderConfig: OrderConfig[];
  isIndented: boolean | undefined;
}

export const getGroupedColumnsToResetSort = ({
  columnState,
  vifOrderConfig,
  isIndented,
}: IColumnGroupResetParams) => {
  if (columnState === undefined) {
    return [];
  }
  const firstGroup = columnState.find((column) => column.rowGroupIndex == 0);
  return columnState
    .map((col): ColumnState | null => {
      if (
        col.colId &&
        col.sort &&
        shouldResetSort({ colId: col.colId, vifOrderConfig, isIndented, firstGroup })
      ) {
        return {
          colId: col.colId,
          sort: null,
          sortIndex: null,
        };
      }
      return null;
    })
    .filter((col): col is ColumnState => col !== null);
};

interface IResetSortParams {
  colId: string;
  vifOrderConfig: OrderConfig[];
  isIndented: boolean | undefined;
  firstGroup: ColumnState | undefined;
}

const shouldResetSort = ({ colId, vifOrderConfig, isIndented, firstGroup }: IResetSortParams) => {
  if (!colId.startsWith(GROUP_COLUMN_PREFIX)) return false;

  // if a column was sorted and is no longer sorted, the map from vif columns to agColumn defs
  // will reset its `sort` attribute properly. but since grouped columns are auto-generated by
  // ag-grid, we need to manually reset their `sort` attribute in the case that the grouped column
  // is no longer marked as sorted on the vif. to do this, we will find any grouped column that
  // ag-grid has marked as sorted, but is not marked as sorted on the vif, and reset its `sort`
  // attribute to null.
  const sortedColumns = new Set(vifOrderConfig.map((o) => o.columnName));
  if (!isIndented) {
    return !sortedColumns.has(colId);
  } else {
    const groupColName = firstGroup?.colId.replace(`${GROUP_COLUMN_PREFIX}-`, '');
    const autoColId = `${GROUP_COLUMN_PREFIX}-${groupColName}`;
    return !sortedColumns.has(autoColId);
  }
};

export const updateColIdForIndentedLayout = (columnState: ColumnState[]) => {
  return columnState.map((column) => ({
    colId: column.colId.startsWith(GROUP_COLUMN_PREFIX) ? GROUP_COLUMN_PREFIX : column.colId,
    sort: column.sort,
    sortIndex: column.sortIndex,
  }));
};

interface IGetNonGroupedColumnsToResetSort {
  columnState: ColumnState[] | undefined;
  vifOrderConfig: OrderConfig[];
}

export const getNonGroupedColumnsToResetSort = ({
  columnState,
  vifOrderConfig,
}: IGetNonGroupedColumnsToResetSort) => {
  if (columnState === undefined) {
    return [];
  }
  return columnState
    .map((column): ColumnState | null => {
      if (column.colId.startsWith(GROUP_COLUMN_PREFIX)) return null;
      const orderConfig = vifOrderConfig.find((order) => order.columnName === column.colId);
      if (!orderConfig) {
        return {
          colId: column.colId,
          sort: null,
          sortIndex: null,
        };
      }
      return null;
    })
    .filter((col): col is ColumnState => col !== null);
};

interface IUpdateColumnSort {
  api: GridApi | undefined;
  vifOrderConfig: OrderConfig[];
  isIndented: boolean | undefined;
}

export const updateColumnsSort = ({ api, vifOrderConfig, isIndented }: IUpdateColumnSort) => {
  let columnSortState = getColumnSortState(vifOrderConfig);
  const columnState = api?.getColumnState();
  const groupedColumnsToResetSort = getGroupedColumnsToResetSort({
    columnState,
    vifOrderConfig,
    isIndented,
  });
  const nonGroupedColumnsToResetSort = getNonGroupedColumnsToResetSort({ columnState, vifOrderConfig });

  columnSortState.push(...groupedColumnsToResetSort);
  columnSortState.push(...nonGroupedColumnsToResetSort);

  // Sample column state:
  // [
  //   {
  //     colId: "ag-Grid-AutoColumn-fieldname",
  //     sort: "desc",
  //     sortIndex: 0
  //   }
  // ]
  // We have to manipulate the colId if isIndented is TRUE
  // since in agGrid, it is expected to have colId: ag-Grid-AutoColumn (without fieldname)
  if (isIndented) {
    columnSortState = updateColIdForIndentedLayout(columnSortState);
  }
  api?.applyColumnState({ state: columnSortState });
};

type GetCurrentSortProps = {
  orderConfig: OrderConfig[] | undefined;
  columnName: string;
};

export const getCurrentSort = (props: GetCurrentSortProps): SortDirection => {
  const { orderConfig, columnName } = props;
  if (!orderConfig) return;
  const colOrder = orderConfig.find((col) => col.columnName === columnName);
  if (!colOrder) return;
  return getSortDirection(colOrder);
};

export type GetNextSortProps = {
  orderConfig: OrderConfig[] | undefined;
  columnName: string;
  customSortConfig: CustomSortConfig | undefined;
  isHeaderClick?: boolean;
};

/**
 *
 * @param orderConfig
 * @returns SortDirection
 *
 * If sortToFollow is undefined, it means the sorting configuration was created
 * when enable_table_column_custom_sorting = FALSE. Therefore, we need to follow the ascending attribute.
 */
export const getSortDirection = (orderConfig: OrderConfig) => {
  if (has(orderConfig, 'sortToFollow')) {
    return orderConfig.sortToFollow;
  } else if (has(orderConfig, 'ascending')) {
    return orderConfig.ascending ? 'ascending' : 'descending';
  }
  return undefined;
};

export const getNextSort = (props: GetNextSortProps): SortDirection => {
  const { orderConfig, columnName, customSortConfig, isHeaderClick = true } = props;
  const currentSort = getCurrentSort({ orderConfig, columnName });
  if (currentSort === 'ascending') return 'descending';
  if (currentSort === 'descending') {
    if (customSortConfig?.[columnName] && customSortConfig?.[columnName].length !== 0) {
      return 'customSort';
    }
    return undefined;
  }
  if (currentSort === 'customSort') {
    if (!isHeaderClick) {
      return 'customSort';
    }
    return undefined;
  }
  return 'ascending';
};

type CreateOrderConfigProps = {
  columnName?: string;
  sortToFollow?: SortDirection;
  ascending?: boolean;
  currentOrderConfig?: OrderConfig;
};

const createOrderConfig = (props: CreateOrderConfigProps): OrderConfig => {
  const { columnName, sortToFollow, ascending, currentOrderConfig } = props;
  const orderConfig: Partial<OrderConfig> = {
    ...(currentOrderConfig ?? {}),
    sortToFollow,
    ascending,
  };
  if (columnName !== undefined) {
    orderConfig.columnName = columnName;
  }
  return orderConfig as OrderConfig;
};

export type UpdateSortConfigProps = {
  configs: OrderConfig[];
  columnName: string;
  nextSort: SortDirection;
  isMultiSort: boolean;
  useAscending?: boolean;
  overrideSortToFollow?: SortDirection;
};

export const updateSortConfig = ({
  configs,
  columnName,
  nextSort,
  isMultiSort,
  useAscending = true,
  overrideSortToFollow,
}: UpdateSortConfigProps): OrderConfig[] => {
  const existingIndex = configs.findIndex((col) => col.columnName === columnName);

  const sortToFollow = overrideSortToFollow ?? nextSort;

  let nextAscending: boolean | undefined = undefined;
  if (useAscending && sortToFollow !== 'customSort') {
    if (sortToFollow === 'ascending') {
      nextAscending = true;
    } else if (sortToFollow === 'descending') {
      nextAscending = false;
    }
  }

  if (isMultiSort) {
    if (existingIndex !== -1) {
      const currentOrderConfig: OrderConfig = configs[existingIndex];
      const newConfigs = configs.filter((_val, index) => index !== existingIndex);
      return [
        ...newConfigs,
        createOrderConfig({
          currentOrderConfig,
          sortToFollow,
          ascending: useAscending ? nextAscending : currentOrderConfig.ascending,
        }),
      ];
    } else {
      return [
        ...configs,
        createOrderConfig({
          columnName,
          sortToFollow,
          ...(useAscending && { ascending: nextAscending }),
        }),
      ];
    }
  } else if (existingIndex !== -1 && sortToFollow === 'customSort') {
    // if the user is about to apply customSort and there is an existing sort for the columnName,
    // it should retain the value of 'ascending' parameter from the currentOrderConfig
    // that way, if the user resets the customSort, it will go back to the previous sort done by the user

    // example:
    // 1. user sorts column A by ascending order
    // 2. then user does custom sort for column A
    // 3. then user resets the custom sort
    // 4. the sort for column A should go back to ascending

    const currentOrderConfig: OrderConfig = configs[existingIndex];

    return [
      {
        columnName,
        sortToFollow,
        ascending: currentOrderConfig.ascending,
      },
    ];
  } else {
    return [
      {
        columnName,
        sortToFollow,
        ...(useAscending && { ascending: nextAscending }),
      },
    ];
  }
};

type UpdateMultipleSortConfigProps = {
  configs: OrderConfig[];
  columnNames: { columnName: string; nextSort: SortDirection }[];
  isMultiSort: boolean;
  overrideSortToFollow?: SortDirection;
};

const resolveAscending = (
  nextSort: SortDirection,
  currentOrderConfig: OrderConfig | undefined,
): boolean | undefined => {
  if (nextSort === 'customSort') return currentOrderConfig?.ascending;
  if (nextSort === 'ascending') return true;
  if (nextSort === 'descending') return false;
  return undefined;
};

const replaceSortConfig = (configs: OrderConfig[], newConfig: OrderConfig): OrderConfig[] => {
  const configsWithoutOld = configs.filter((config) => config.columnName !== newConfig.columnName);
  return [...configsWithoutOld, newConfig];
};

export const updateMultipleSortConfig = ({
  configs,
  columnNames,
  isMultiSort,
  overrideSortToFollow,
}: UpdateMultipleSortConfigProps): OrderConfig[] => {
  let updatedConfigs: OrderConfig[] = isMultiSort ? [...configs] : [];

  columnNames.forEach(({ columnName, nextSort }) => {
    const sortToFollow = overrideSortToFollow ?? nextSort;
    const currentOrderConfig = configs.find((col) => col.columnName === columnName);
    const ascending = resolveAscending(nextSort, currentOrderConfig);

    if (isMultiSort) {
      const existingIndex = updatedConfigs.findIndex((col) => col.columnName === columnName);
      const newConfig = createOrderConfig({ columnName, sortToFollow, ascending });

      if (existingIndex !== -1) {
        updatedConfigs = replaceSortConfig(updatedConfigs, newConfig);
      } else if (nextSort) {
        updatedConfigs.push(newConfig);
      }
    } else {
      if (nextSort !== undefined) {
        const newConfig = createOrderConfig({ columnName, sortToFollow, ascending });
        updatedConfigs.push(newConfig);
      }
    }
  });

  return updatedConfigs;
};

type GetGroupedOrderConfigProps = {
  hierarchies: HierarchyColumnConfig[] | undefined;
  orderConfig: OrderConfig[];
};

export const getGroupedOrderConfig = (props: GetGroupedOrderConfigProps) => {
  const { hierarchies, orderConfig } = props;
  if (!hierarchies) return;
  const groupingColumnNames = hierarchies
    .filter((hierarchy) => hierarchy.isGrouping)
    .map((hierarchy) => hierarchy.columnName);
  const groupings = orderConfig.filter((c) => groupingColumnNames.includes(c.columnName));
  return groupings;
};

export type HandleHeaderSortIndexForUnindentedProps = {
  hierarchies: HierarchyColumnConfig[];
  orderConfig: OrderConfig[];
  hierarchyId: string | undefined;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
};

export enum HEADER_SORT_INDEX {
  DEFAULT = 0,
  ASCENDING = 1,
  DESCENDING = 2,
  CUSTOM_SORT = 3,
}

export const handleHeaderSortIndexForUnindented = (props: HandleHeaderSortIndexForUnindentedProps) => {
  const { hierarchies, orderConfig, hierarchyId, handleIndentedHeaderSortIndex } = props;
  const groupedOrderConfig = getGroupedOrderConfig({
    hierarchies,
    orderConfig,
  });
  if (!groupedOrderConfig || !handleIndentedHeaderSortIndex) {
    return;
  }
  const sortValues = groupedOrderConfig.map((config) => config.sortToFollow);
  const allAscending = sortValues.every((val) => val === 'ascending');
  const allDescending = sortValues.every((val) => val === 'descending');
  const hasCustomSort = sortValues.includes('customSort');
  if (allAscending) {
    handleIndentedHeaderSortIndex(HEADER_SORT_INDEX.ASCENDING, hierarchyId);
  } else if (allDescending) {
    handleIndentedHeaderSortIndex(HEADER_SORT_INDEX.DESCENDING, hierarchyId);
  } else if (hasCustomSort || new Set(sortValues).size > 1) {
    handleIndentedHeaderSortIndex(HEADER_SORT_INDEX.CUSTOM_SORT, hierarchyId);
  }
};

export const getRowGroupColumnForSort = (target: HTMLElement, rowGroupColumns: Column[] | undefined) => {
  if (isEmpty(rowGroupColumns) || isUndefined(rowGroupColumns)) {
    return;
  }
  // When columns clicked under Row Groups
  const closestOption = target.closest('.ag-column-drop-cell.ag-column-drop-vertical-cell') as HTMLElement;
  if (!closestOption) {
    return;
  }
  const optionLabelElement = closestOption.querySelector(
    '.ag-column-drop-cell-text.ag-column-drop-vertical-cell-text',
  ) as HTMLElement;
  const columnLabel = optionLabelElement.innerHTML.trim();
  const agColumn = find(rowGroupColumns, (column) => column.getColDef().headerName == columnLabel);
  return agColumn;
};

export const isSortMenuOrResizeClick = (target: HTMLElement): boolean => {
  return Boolean(target.closest('.ag-header-cell-menu-button') || target.closest('.ag-header-cell-resize'));
};

export const getColumnId = (
  headerCell: HTMLElement | null,
  isIndented?: boolean | undefined,
  rowGroupColumnId?: string,
  isFirstHierarchyColumn?: boolean,
): string | null => {
  if (headerCell) {
    return headerCell.getAttribute('col-id');
  }
  if (isIndented && rowGroupColumnId && isFirstHierarchyColumn) {
    return GROUP_COLUMN_PREFIX;
  }
  return rowGroupColumnId ?? null;
};

export type HandleIndentedColumnSortProps = {
  isMultiSort: boolean;
  headerSortCycleIndex: number;
  currentSortState: OrderConfig[];
  hierarchy?: Hierarchy;
  hierarchyColumns: HierarchyColumnConfig[];
  setHeaderSortCycleIndex: React.Dispatch<React.SetStateAction<number>>;
  setCurrentSortState: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  activeHierarchyId: string | undefined;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
  sortSourceElement: string | null;
  rowGroupColumnId: string | undefined;
};

export const handleIndentedColumnSort = (props: HandleIndentedColumnSortProps) => {
  const {
    isMultiSort,
    headerSortCycleIndex,
    currentSortState,
    hierarchy,
    hierarchyColumns,
    setHeaderSortCycleIndex,
    setCurrentSortState,
    handleCustomSortCycle,
    activeHierarchyId,
    handleIndentedHeaderSortIndex,
    rowGroupColumnId,
    sortSourceElement,
  } = props;
  const nextIndex = (headerSortCycleIndex + 1) % HEADER_SORT_CYCLE.length;
  const groupedOrderConfig = getGroupedOrderConfig({
    hierarchies: hierarchyColumns,
    orderConfig: currentSortState,
  });
  if (!groupedOrderConfig) return;

  const sortDirections = groupedOrderConfig.map((g) =>
    getNextSort({
      orderConfig: currentSortState,
      columnName: g.columnName,
      customSortConfig: hierarchy?.columnSortConfig,
    }),
  );

  let headerCycleIndex = nextIndex;
  const isNextMixed = HEADER_SORT_CYCLE[headerCycleIndex] === 'mixed';
  const hasCustomSort = sortDirections.includes('customSort');

  if (isNextMixed && !hasCustomSort) {
    headerCycleIndex = (headerCycleIndex + 1) % HEADER_SORT_CYCLE.length;
  }

  const nextHeaderSortCycle = HEADER_SORT_CYCLE[headerCycleIndex];

  let groupedColumns = hierarchyColumns.filter((h) => h.isGrouping);
  if (sortSourceElement == 'row-groups') {
    groupedColumns = groupedColumns.filter((h) => h.columnName == rowGroupColumnId);
  }
  const columnsToUpdate = groupedColumns.map((hierarchyColumn) => {
    const columnName = hierarchyColumn.columnName;
    const hasCustomConfig = hierarchy?.columnSortConfig?.hasOwnProperty(columnName);
    const nextSort: SortDirection | undefined =
      nextHeaderSortCycle === 'mixed' ? (hasCustomConfig ? 'customSort' : undefined) : nextHeaderSortCycle;
    return { columnName, nextSort };
  });

  const updatedSortConfig = updateMultipleSortConfig({
    configs: currentSortState,
    columnNames: columnsToUpdate,
    isMultiSort,
  });

  setHeaderSortCycleIndex(headerCycleIndex);
  setCurrentSortState(updatedSortConfig);
  handleCustomSortCycle(updatedSortConfig, activeHierarchyId);

  if (handleIndentedHeaderSortIndex) {
    handleIndentedHeaderSortIndex(headerCycleIndex, activeHierarchyId);
  }
};

type HandleRegularColumnSortProps = {
  colId: string;
  isMultiSort: boolean;
  currentSortState: OrderConfig[];
  hierarchy?: Hierarchy;
  hierarchyColumns: HierarchyColumnConfig[];
  setHeaderSortCycleIndex: React.Dispatch<React.SetStateAction<number>>;
  setCurrentSortState: React.Dispatch<React.SetStateAction<OrderConfig[]>>;
  handleCustomSortCycle: (newOrderConfig: OrderConfig[], hierarchyId?: string) => void;
  activeHierarchyId: string | undefined;
  handleIndentedHeaderSortIndex?: (headerSortCycleIndex: number, hierarchyId?: string) => void;
  renderUnindentedHeader: (
    renderUnindentedHeaderProps: RenderUnindentedHeaderProps,
  ) => (() => void) | undefined;
  sortToFollow?: SortDirection;
  isIndentedConfigured: boolean | undefined;
  gridRefWrapper: React.RefObject<HTMLDivElement>;
  gridApi: GridApi | null;
  vifOrderConfig: OrderConfig[];
  headerSortCycleIndex: number;
};

export const handleRegularColumnSort = (props: HandleRegularColumnSortProps) => {
  const {
    colId,
    isMultiSort,
    currentSortState,
    hierarchyColumns,
    hierarchy,
    activeHierarchyId,
    setCurrentSortState,
    setHeaderSortCycleIndex,
    handleCustomSortCycle,
    handleIndentedHeaderSortIndex,
    renderUnindentedHeader,
    gridApi,
    gridRefWrapper,
    vifOrderConfig,
    isIndentedConfigured,
  } = props;
  let parsedColId = colId;
  const isUnindented = colId.startsWith(GROUP_COLUMN_PREFIX);

  if (isUnindented) {
    parsedColId = colId.replace(`${GROUP_COLUMN_PREFIX}-`, '');
  }

  const nextSort = getNextSort({
    orderConfig: currentSortState,
    columnName: parsedColId,
    customSortConfig: hierarchy?.columnSortConfig,
  });

  const updatedSortConfig = updateSortConfig({
    configs: currentSortState,
    columnName: parsedColId,
    nextSort,
    isMultiSort,
    useAscending: true,
  });

  setCurrentSortState(updatedSortConfig);
  handleCustomSortCycle(updatedSortConfig, activeHierarchyId);

  if (!isMultiSort) {
    setHeaderSortCycleIndex(HEADER_SORT_INDEX.DEFAULT);
    if (handleIndentedHeaderSortIndex) {
      handleIndentedHeaderSortIndex(HEADER_SORT_INDEX.DEFAULT, activeHierarchyId);
    }
  }

  if (isUnindented) {
    handleHeaderSortIndexForUnindented({
      hierarchies: hierarchyColumns,
      orderConfig: updatedSortConfig,
      hierarchyId: activeHierarchyId,
      handleIndentedHeaderSortIndex,
    });

    renderUnindentedHeader({
      colId: parsedColId,
      sortToFollow: nextSort,
      isIndented: isIndentedConfigured,
      gridRefWrapper,
      gridApi,
      vifOrderConfig,
      hierarchyColumns,
    });
  }
};

type TranslateSortToHeaderSortCycleProps = {
  vifOrderConfig: OrderConfig[];
  rowGroupColumnId: string | undefined;
};

export const translateSortToHeaderSortCycle = (
  props: TranslateSortToHeaderSortCycleProps,
): HeaderSortType => {
  const { vifOrderConfig, rowGroupColumnId } = props;
  const orderConfig = vifOrderConfig.find((o) => o.columnName === rowGroupColumnId);
  if (!orderConfig || !orderConfig.sortToFollow || orderConfig.sortToFollow === 'default') {
    return null;
  }
  if (orderConfig.sortToFollow === 'customSort') {
    return 'mixed';
  }
  return orderConfig.sortToFollow;
};
